import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  AdventureMainCategory,
  AdventureStatus,
} from '../types/adventure-editor.types'
import type {
  AdventureDto,
  AdventureGeneralInfoDto,
  CategoryWithTagsDto,
  CriteriaItem,
  CriteriaLogicGroup,
  LocationFlowData,
  TagCategoryDto,
  UnlockRequirement,
} from '../types/adventure-editor.types'
import type { AdventureCategoryDto } from '@api/types/adventure'
import { WorkflowConditionType, getDefaultConditionConfig, type DefaultConditionConfigOptions } from '../types/workflow.types'

// ====================
// STATE INTERFACE
// ====================

export interface AdventureEditorState {
  // Core adventure data
  adventureId?: string
  adventure?: AdventureDto

  // Editable form data
  generalInfo: AdventureGeneralInfoDto

  // Category settings (loaded from API based on mainCategory)
  categorySettings: AdventureCategoryDto | null

  // Tags and categories
  availableCategories: CategoryWithTagsDto[]
  adventureTypeCategories: TagCategoryDto[]
  isLoadingTags: boolean

  // Location flow
  locationFlow: LocationFlowData[]

  // Requirements (legacy)
  displayCriteria: UnlockRequirement[]
  joinCriteria: UnlockRequirement[]
  useDisplayForJoining: boolean

  // Criteria (new workflow-based system)
  displayCriteriaItems: CriteriaItem[]
  displayCriteriaLogicGroups: CriteriaLogicGroup[]
  joinCriteriaItems: CriteriaItem[]
  joinCriteriaLogicGroups: CriteriaLogicGroup[]

  // UI state
  activeSection:
    | 'description'
    | 'settings'
    | 'groups'
    | 'tags'
    | 'images'
    | 'codes'
    | 'maintainers'
    | 'moderators'
    | 'testers'
    | 'display'
    | 'join'
    | 'locationflow'
    | 'mapview'
  isLoading: boolean
  isDirty: boolean

  // Actions
  loadAdventure: (adventure: AdventureDto) => void
  loadCategorySettings: (categorySettings: AdventureCategoryDto | null) => void
  updateGeneralInfo: (updates: Partial<AdventureGeneralInfoDto>) => void
  updateAdventure: (updates: Partial<AdventureDto>) => void
  setActiveSection: (
    section:
      | 'description'
      | 'settings'
      | 'groups'
      | 'tags'
      | 'images'
      | 'codes'
      | 'maintainers'
      | 'moderators'
      | 'testers'
      | 'display'
      | 'join'
      | 'locationflow'
      | 'mapview'
  ) => void
  toggleTag: (tagId: string, categorySelectionMode: 'single' | 'multiple', categoryId: string) => void
  removeTag: (tagId: string) => void
  loadAvailableCategories: (categories: CategoryWithTagsDto[]) => void
  loadAdventureTypeCategories: (categories: TagCategoryDto[]) => void
  setIsLoadingTags: (isLoading: boolean) => void
  updateLocationFlow: (flow: LocationFlowData[]) => void
  addLocation: (location: LocationFlowData) => void
  updateLocation: (locationId: string, updates: Partial<LocationFlowData>) => void
  removeLocation: (locationId: string) => void
  updateDisplayCriteria: (criteria: UnlockRequirement[]) => void
  updateJoinCriteria: (criteria: UnlockRequirement[]) => void
  setUseDisplayForJoining: (value: boolean) => void

  // Criteria actions (new workflow-based system)
  addCriteriaItem: (type: 'display' | 'join', conditionType: WorkflowConditionType, logicGroupId?: string) => void
  updateCriteriaItem: (type: 'display' | 'join', itemId: string, config: Record<string, unknown>) => void
  removeCriteriaItem: (type: 'display' | 'join', itemId: string) => void
  reorderCriteriaItems: (type: 'display' | 'join', items: CriteriaItem[]) => void
  addCriteriaLogicGroup: (type: 'display' | 'join', operator?: 'AND' | 'OR') => string
  updateCriteriaLogicGroup: (type: 'display' | 'join', groupId: string, operator: 'AND' | 'OR') => void
  removeCriteriaLogicGroup: (type: 'display' | 'join', groupId: string) => void
  setCriteriaItems: (type: 'display' | 'join', items: CriteriaItem[], groups: CriteriaLogicGroup[]) => void
  setIsLoading: (isLoading: boolean) => void
  setIsDirty: (isDirty: boolean) => void
  reset: () => void
}

// ====================
// INITIAL STATE
// ====================

const createInitialGeneralInfo = (): AdventureGeneralInfoDto => ({
  title: '',
  quickDescription: '',
  fullDescription: '',
  mainCategory: AdventureMainCategory.TraditionalGeoCaches,
  startDate: null,
  endDate: null,
  latitude: null,
  longitude: null,
  isPublic: true,
  allowGroups: false,
  maxParticipants: 0,
  isRepeatable: false,
  difficultyLevel: 1,
  groupParticipationSettings: undefined,
  // Silent adventure fields
  isSilentJoin: false,
  isSilentComplete: false,
  silentCompleteDurationMinutes: undefined,
})

const initialState = {
  adventureId: undefined,
  adventure: undefined,
  generalInfo: createInitialGeneralInfo(),
  categorySettings: null as AdventureCategoryDto | null,
  availableCategories: [],
  adventureTypeCategories: [],
  isLoadingTags: false,
  locationFlow: [],
  displayCriteria: [],
  joinCriteria: [],
  useDisplayForJoining: false,
  // New workflow-based criteria
  displayCriteriaItems: [] as CriteriaItem[],
  displayCriteriaLogicGroups: [] as CriteriaLogicGroup[],
  joinCriteriaItems: [] as CriteriaItem[],
  joinCriteriaLogicGroups: [] as CriteriaLogicGroup[],
  activeSection: 'description' as const,
  isLoading: false,
  isDirty: false,
}

// Helper to generate unique IDs (with fallback for browsers without crypto.randomUUID)
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback UUID v4 implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// ====================
// STORE
// ====================

export const useAdventureEditorStore = create<AdventureEditorState>()(
  devtools(
    (set) => ({
      ...initialState,

      // Load adventure from API
      loadAdventure: (adventure: AdventureDto) => {
        set(
          (state) => {
            // Preserve existing tags if the incoming adventure doesn't have them
            // (e.g., after save, backend update response may not include tags)
            const tags = adventure.tags && adventure.tags.length > 0
              ? adventure.tags
              : (state.adventure?.tags || [])

            return {
              adventureId: adventure.id,
              adventure: { ...adventure, tags },
              generalInfo: {
                title: adventure.title,
                quickDescription: adventure.quickDescription || '',
                fullDescription: adventure.fullDescription || '',
                mainCategory: adventure.mainCategory,
                startDate: adventure.startDate ? new Date(adventure.startDate) : null,
                endDate: adventure.endDate ? new Date(adventure.endDate) : null,
                latitude: adventure.latitude ?? null,
                longitude: adventure.longitude ?? null,
                isPublic: adventure.isPublic,
                allowGroups: adventure.allowGroups,
                maxParticipants: adventure.maxParticipants,
                isRepeatable: adventure.isRepeatable,
                difficultyLevel: adventure.difficultyLevel,
                groupParticipationSettings: adventure.groupParticipationSettings,
                // Silent adventure fields
                isSilentJoin: (adventure as any).isSilentJoin ?? false,
                isSilentComplete: (adventure as any).isSilentComplete ?? false,
                silentCompleteDurationMinutes: (adventure as any).silentCompleteDurationMinutes,
              },
              useDisplayForJoining: adventure.useDisplayForJoining ?? false,
              isDirty: false,
            }
          },
          false,
          'loadAdventure'
        )
      },

      // Load category settings
      loadCategorySettings: (categorySettings: AdventureCategoryDto | null) => {
        set({ categorySettings }, false, 'loadCategorySettings')
      },

      // Update general info (partial updates)
      updateGeneralInfo: (updates: Partial<AdventureGeneralInfoDto>) => {
        set(
          (state) => ({
            generalInfo: { ...state.generalInfo, ...updates },
            isDirty: true,
          }),
          false,
          'updateGeneralInfo'
        )
      },

      // Update adventure (partial updates)
      updateAdventure: (updates: Partial<AdventureDto>) => {
        set(
          (state) => ({
            adventure: state.adventure ? { ...state.adventure, ...updates } : undefined,
            isDirty: true,
          }),
          false,
          'updateAdventure'
        )
      },

      // Set active section
      setActiveSection: (section) => {
        set({ activeSection: section }, false, 'setActiveSection')
      },

      // Toggle tag (handle single vs multiple selection)
      toggleTag: (tagId: string, categorySelectionMode: 'single' | 'multiple', categoryId: string) => {
        set(
          (state) => {
            if (!state.adventure) return state

            const currentTags = [...(state.adventure.tags || [])]

            if (categorySelectionMode === 'single') {
              // Remove all other tags from this category first
              const category = state.availableCategories.find((c) => c.id === categoryId)
              if (category) {
                const categoryTagIds = category.tags.map((t) => t.id)
                const filteredTags = currentTags.filter((t) => !categoryTagIds.includes(t))

                // Add the new tag if not already present
                if (!filteredTags.includes(tagId)) {
                  filteredTags.push(tagId)
                }

                return {
                  adventure: { ...state.adventure, tags: filteredTags },
                  isDirty: true,
                }
              }
            } else {
              // Multiple selection - toggle
              const index = currentTags.indexOf(tagId)
              if (index > -1) {
                currentTags.splice(index, 1)
              } else {
                currentTags.push(tagId)
              }

              return {
                adventure: { ...state.adventure, tags: currentTags },
                isDirty: true,
              }
            }

            return state
          },
          false,
          'toggleTag'
        )
      },

      // Remove tag
      removeTag: (tagId: string) => {
        set(
          (state) => {
            if (!state.adventure) return state

            return {
              adventure: {
                ...state.adventure,
                tags: (state.adventure.tags || []).filter((t) => t !== tagId),
              },
              isDirty: true,
            }
          },
          false,
          'removeTag'
        )
      },

      // Load available categories
      loadAvailableCategories: (categories: CategoryWithTagsDto[]) => {
        set({ availableCategories: categories }, false, 'loadAvailableCategories')
      },

      // Load adventure type categories
      loadAdventureTypeCategories: (categories: TagCategoryDto[]) => {
        set({ adventureTypeCategories: categories }, false, 'loadAdventureTypeCategories')
      },

      // Set loading tags flag
      setIsLoadingTags: (isLoading: boolean) => {
        set({ isLoadingTags: isLoading }, false, 'setIsLoadingTags')
      },

      // Update location flow
      updateLocationFlow: (flow: LocationFlowData[]) => {
        set({ locationFlow: flow, isDirty: true }, false, 'updateLocationFlow')
      },

      // Add location
      addLocation: (location: LocationFlowData) => {
        set(
          (state) => ({
            locationFlow: [...state.locationFlow, location],
            isDirty: true,
          }),
          false,
          'addLocation'
        )
      },

      // Update location
      updateLocation: (locationId: string, updates: Partial<LocationFlowData>) => {
        set(
          (state) => ({
            locationFlow: state.locationFlow.map((loc) =>
              loc.id === locationId ? { ...loc, ...updates } : loc
            ),
            isDirty: true,
          }),
          false,
          'updateLocation'
        )
      },

      // Remove location
      removeLocation: (locationId: string) => {
        set(
          (state) => ({
            locationFlow: state.locationFlow.filter((loc) => loc.id !== locationId),
            isDirty: true,
          }),
          false,
          'removeLocation'
        )
      },

      // Update display criteria
      updateDisplayCriteria: (criteria: UnlockRequirement[]) => {
        set({ displayCriteria: criteria, isDirty: true }, false, 'updateDisplayCriteria')
      },

      // Update join criteria
      updateJoinCriteria: (criteria: UnlockRequirement[]) => {
        set({ joinCriteria: criteria, isDirty: true }, false, 'updateJoinCriteria')
      },

      // Set use display for joining flag
      setUseDisplayForJoining: (value: boolean) => {
        set({ useDisplayForJoining: value, isDirty: true }, false, 'setUseDisplayForJoining')
      },

      // ========================
      // NEW CRITERIA ACTIONS
      // ========================

      // Add a new criteria item
      addCriteriaItem: (type: 'display' | 'join', conditionType: WorkflowConditionType, logicGroupId?: string) => {
        set(
          (state) => {
            const itemsKey = type === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
            const currentItems = state[itemsKey]
            const maxOrder = currentItems.length > 0
              ? Math.max(...currentItems.map((item) => item.order))
              : -1

            // Get adventure location for default config
            const configOptions: DefaultConditionConfigOptions = {
              adventureLatitude: state.generalInfo.latitude,
              adventureLongitude: state.generalInfo.longitude,
            }

            const newItem: CriteriaItem = {
              id: generateId(),
              conditionType,
              config: getDefaultConditionConfig(conditionType, configOptions),
              logicGroupId,
              order: maxOrder + 1,
            }

            return {
              [itemsKey]: [...currentItems, newItem],
              isDirty: true,
            }
          },
          false,
          'addCriteriaItem'
        )
      },

      // Update a criteria item's config
      updateCriteriaItem: (type: 'display' | 'join', itemId: string, config: Record<string, unknown>) => {
        set(
          (state) => {
            const itemsKey = type === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
            return {
              [itemsKey]: state[itemsKey].map((item) =>
                item.id === itemId ? { ...item, config } : item
              ),
              isDirty: true,
            }
          },
          false,
          'updateCriteriaItem'
        )
      },

      // Remove a criteria item
      removeCriteriaItem: (type: 'display' | 'join', itemId: string) => {
        set(
          (state) => {
            const itemsKey = type === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
            return {
              [itemsKey]: state[itemsKey].filter((item) => item.id !== itemId),
              isDirty: true,
            }
          },
          false,
          'removeCriteriaItem'
        )
      },

      // Reorder criteria items (for drag-drop)
      reorderCriteriaItems: (type: 'display' | 'join', items: CriteriaItem[]) => {
        set(
          () => {
            const itemsKey = type === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
            // Update order based on array position
            const reorderedItems = items.map((item, index) => ({
              ...item,
              order: index,
            }))
            return {
              [itemsKey]: reorderedItems,
              isDirty: true,
            }
          },
          false,
          'reorderCriteriaItems'
        )
      },

      // Add a new logic group (isAnd: true = AND, false = OR)
      addCriteriaLogicGroup: (type: 'display' | 'join', operator: 'AND' | 'OR' = 'AND') => {
        const groupId = generateId()
        set(
          (state) => {
            const groupsKey = type === 'display' ? 'displayCriteriaLogicGroups' : 'joinCriteriaLogicGroups'
            const currentGroups = state[groupsKey]
            const maxOrderIndex = currentGroups.length > 0
              ? Math.max(...currentGroups.map((g) => g.orderIndex))
              : -1

            const newGroup: CriteriaLogicGroup = {
              id: groupId,
              isAnd: operator === 'AND',
              orderIndex: maxOrderIndex + 1,
            }

            return {
              [groupsKey]: [...currentGroups, newGroup],
              isDirty: true,
            }
          },
          false,
          'addCriteriaLogicGroup'
        )
        return groupId
      },

      // Update a logic group's operator (isAnd)
      updateCriteriaLogicGroup: (type: 'display' | 'join', groupId: string, operator: 'AND' | 'OR') => {
        set(
          (state) => {
            const groupsKey = type === 'display' ? 'displayCriteriaLogicGroups' : 'joinCriteriaLogicGroups'
            return {
              [groupsKey]: state[groupsKey].map((group) =>
                group.id === groupId ? { ...group, isAnd: operator === 'AND' } : group
              ),
              isDirty: true,
            }
          },
          false,
          'updateCriteriaLogicGroup'
        )
      },

      // Remove a logic group (and all items in it)
      removeCriteriaLogicGroup: (type: 'display' | 'join', groupId: string) => {
        set(
          (state) => {
            const groupsKey = type === 'display' ? 'displayCriteriaLogicGroups' : 'joinCriteriaLogicGroups'
            const itemsKey = type === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
            return {
              [groupsKey]: state[groupsKey].filter((group) => group.id !== groupId),
              [itemsKey]: state[itemsKey].filter((item) => item.logicGroupId !== groupId),
              isDirty: true,
            }
          },
          false,
          'removeCriteriaLogicGroup'
        )
      },

      // Set all criteria items and groups at once (for loading from API)
      setCriteriaItems: (type: 'display' | 'join', items: CriteriaItem[], groups: CriteriaLogicGroup[]) => {
        set(
          () => {
            const itemsKey = type === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
            const groupsKey = type === 'display' ? 'displayCriteriaLogicGroups' : 'joinCriteriaLogicGroups'
            return {
              [itemsKey]: items,
              [groupsKey]: groups,
            }
          },
          false,
          'setCriteriaItems'
        )
      },

      // Set loading flag
      setIsLoading: (isLoading: boolean) => {
        set({ isLoading }, false, 'setIsLoading')
      },

      // Set dirty flag
      setIsDirty: (isDirty: boolean) => {
        set({ isDirty }, false, 'setIsDirty')
      },

      // Reset to initial state (but preserve loaded tag categories)
      reset: () => {
        set((state) => ({
          ...initialState,
          // Preserve tag categories - they're loaded once and shared across adventures
          availableCategories: state.availableCategories,
          adventureTypeCategories: state.adventureTypeCategories,
          // Reset category settings - will be reloaded for next adventure
          categorySettings: null,
          // Reset criteria to empty arrays
          displayCriteriaItems: [],
          displayCriteriaLogicGroups: [],
          joinCriteriaItems: [],
          joinCriteriaLogicGroups: [],
        }), false, 'reset')
      },
    }),
    { name: 'AdventureEditorStore' }
  )
)

// ====================
// SELECTORS (Optional - for better performance)
// ====================

export const selectGeneralInfo = (state: AdventureEditorState) => state.generalInfo
export const selectActiveSection = (state: AdventureEditorState) => state.activeSection
export const selectLocationFlow = (state: AdventureEditorState) => state.locationFlow
export const selectTags = (state: AdventureEditorState) => state.adventure?.tags || []
export const selectIsDirty = (state: AdventureEditorState) => state.isDirty
export const selectCategorySettings = (state: AdventureEditorState) => state.categorySettings
export const selectCanEditTitle = (state: AdventureEditorState) => {
  // Title can be edited for new adventures or while in Draft status
  // Once published (or completed/cancelled), title is locked
  if (!state.adventure) return true
  const status = state.adventure.status
  // Handle both numeric and string status values from API
  const numStatus = typeof status === 'string' ? parseInt(status, 10) : status
  return numStatus === AdventureStatus.Draft || isNaN(numStatus as number)
}

// ====================
// PUBLISHED STATE SELECTORS
// ====================

/**
 * Check if adventure is in published state
 */
export const selectIsPublished = (state: AdventureEditorState) => {
  if (!state.adventure) return false
  const status = state.adventure.status
  const numStatus = typeof status === 'string' ? parseInt(status, 10) : status
  return numStatus === AdventureStatus.Published
}

/**
 * Check if adventure is in testing phase (can show Publish action)
 */
export const selectIsInTestingPhase = (state: AdventureEditorState) => {
  if (!state.adventure) return false
  const status = state.adventure.status
  const numStatus = typeof status === 'string' ? parseInt(status, 10) : status
  return numStatus === AdventureStatus.Testing
}

/**
 * Can show Save action - hidden when published
 */
export const selectCanSave = (state: AdventureEditorState) => {
  return !selectIsPublished(state)
}

/**
 * Can show Publish action - only during testing phases
 */
export const selectCanPublish = (state: AdventureEditorState) => {
  return selectIsInTestingPhase(state)
}

/**
 * Can Delete - disabled when published
 */
export const selectCanDelete = (state: AdventureEditorState) => {
  return !selectIsPublished(state)
}
